import type { Brand } from "@shared/brand.types";
import { type FormEvent, useRef, useState } from "react";
import { Button } from "@/components/ds/Button";
import { copy } from "@/lib/copy";
import { fetchJson, HTTP_TIMEOUT_LONG_MS, HttpError } from "@/lib/http";

export type ExtractionResult = {
	companyName?: string;
	fields: Record<string, string>;
	missing: string[];
};

type Mode = "paste" | "link";

/**
 * L'entrée principale : coller l'annonce, plutôt que remplir dix cases.
 *
 * Une entreprise a presque toujours son annonce déjà écrite — dans un mail, sur son
 * site, dans un PDF. Lui demander de la ressaisir est la friction réelle du dépôt ;
 * le formulaire vient après, pré-rempli, pour vérifier et corriger.
 *
 * Le lien vers la saisie manuelle est TOUJOURS visible, au même niveau. Une
 * entreprise qui n'a pas d'annonce écrite ne doit pas avoir à deviner qu'un autre
 * chemin existe, ni à passer par une lecture qui ne lui servira à rien.
 */
export function PasteStep({
	brand,
	onExtracted,
	onSkip,
}: {
	brand: Brand;
	onExtracted: (result: ExtractionResult) => void;
	onSkip: () => void;
}) {
	const [mode, setMode] = useState<Mode>("paste");
	const [text, setText] = useState("");
	const [url, setUrl] = useState("");
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const fileInput = useRef<HTMLInputElement>(null);

	/** Un objet part en JSON, un `FormData` part tel quel : `fetchJson` distingue les deux. */
	async function send(body: unknown) {
		setBusy(true);
		setMessage(null);
		try {
			const result = await fetchJson<ExtractionResult>(`/api/extract/${brand.slug}`, {
				method: "POST",
				body,
				// La lecture appelle un modèle de langage. Au délai ordinaire de 15 s, une
				// lecture qui allait aboutir serait coupée juste avant sa réponse.
				timeoutMs: HTTP_TIMEOUT_LONG_MS,
			});
			onExtracted(result);
		} catch (error) {
			// Le serveur nomme ce qui bloque (« Ce PDF ne contient pas de texte lisible »),
			// et ce message vaut mieux que le nôtre, qui ne dit que l'échec.
			const precis =
				error instanceof HttpError && error.failure.kind === "http" ? error.failure.message : null;
			setMessage(precis ?? copy.paste.failed);
		} finally {
			setBusy(false);
		}
	}

	const submit = (event: FormEvent) => {
		event.preventDefault();
		void send(mode === "link" ? { url: url.trim() } : { text: text.trim() });
	};

	const sendFile = (file: File) => {
		const form = new FormData();
		form.append("file", file);
		void send(form);
	};

	const ready = mode === "link" ? url.trim().length > 8 : text.trim().length > 40;

	return (
		<form onSubmit={submit} className="flex flex-col gap-5">
			<div className="flex gap-1 rounded-field bg-mg-subtle p-1">
				{(["paste", "link"] as const).map((value) => (
					<button
						key={value}
						type="button"
						onClick={() => setMode(value)}
						aria-pressed={mode === value}
						className={`t-body-md flex-1 rounded-field px-3 py-2 transition ${
							mode === value ? "bg-mg-surface text-mg-heading shadow-card" : "text-mg-secondary"
						}`}
					>
						{value === "paste" ? copy.paste.tabText : copy.paste.tabLink}
					</button>
				))}
			</div>

			{mode === "paste" ? (
				<textarea
					aria-label={copy.paste.tabText}
					rows={11}
					className="mg-field resize-y"
					placeholder={copy.paste.placeholder}
					value={text}
					onChange={(event) => setText(event.target.value)}
				/>
			) : (
				<input
					type="url"
					aria-label={copy.paste.tabLink}
					className="mg-field"
					placeholder="https://…"
					value={url}
					onChange={(event) => setUrl(event.target.value)}
				/>
			)}

			{message ? (
				<p role="alert" className="t-body-md text-mg-error-fg">
					{message}
				</p>
			) : null}

			<Button tone="primary" full type="submit" disabled={busy || !ready}>
				{busy ? copy.paste.reading : copy.paste.submit}
			</Button>

			<div className="flex flex-wrap items-center justify-between gap-3 border-t border-mg-border-subtle pt-5">
				<button
					type="button"
					onClick={() => fileInput.current?.click()}
					className="t-body text-mg-action underline underline-offset-2"
				>
					{copy.paste.pdfCta}
				</button>
				<button
					type="button"
					onClick={onSkip}
					className="t-body text-mg-secondary underline underline-offset-2 hover:text-mg-heading"
				>
					{copy.paste.manualCta}
				</button>
			</div>

			{/* DEROGATION DS : un input file natif est caché derrière un bouton, il n'a pas de rendu propre. */}
			<input
				ref={fileInput}
				type="file"
				accept="application/pdf"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];
					if (file) sendFile(file);
					event.target.value = "";
				}}
			/>
		</form>
	);
}
